import React, { useCallback, useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  Users,
  Search,
  Plus,
  Edit,
  Trash2,
  RotateCcw,
  History,
  XCircle,
  BarChart2,
  User,
  Clock,
  Phone,
  Mail,
  CreditCard,
  AlertTriangle,
  CheckCircle,
  Truck,
} from "lucide-react";
import { format, differenceInDays, parseISO } from "date-fns";
import Loader from "../../loader/Loader";

import AuditInfoModal, { buildAuditRows } from "../../shared/AuditInfoModal";
import Pagination from "../Pagination";
import { domain } from "../../../security";
import { selectUserID } from "../../../redux/IchthusSlice";
import licensePlaceholder from "../../../Images/profile.jpg";
import Addlogisticspersonnel from "./AddLogisticsPersonnel";

// ─── Helpers ─────────────────────────────────────────────────────────────────
const formatDate = (d) => {
  if (!d) return "—";
  return format(new Date(d), "MMM dd, yyyy");
};
const getFullName = (item) =>
  [item?.firstName, item?.lastName].filter(Boolean).join(" ") || "—";
const getInitials = (item) =>
  [item?.firstName?.[0] || "", item?.lastName?.[0] || ""]
    .join("")
    .toUpperCase();

const getLicenseStatus = (expiryDate) => {
  if (!expiryDate) return null;
  const days = differenceInDays(parseISO(expiryDate), new Date());
  if (days < 0) return "expired";
  if (days <= 30) return "soon";
  return "ok";
};

// ─── Badges ──────────────────────────────────────────────────────────────────
const RoleBadge = ({ role }) => {
  const base =
    "inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold";
  if (role === "Driver")
    return (
      <span className={`${base} bg-indigo-50 text-indigo-700`}>
        <Truck size={10} /> Driver
      </span>
    );
  return (
    <span className={`${base} bg-slate-100 text-slate-600`}>
      <User size={10} /> Personnel
    </span>
  );
};

const StatusBadge = ({ status }) => {
  const base =
    "inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold";
  if (status === "Active")
    return <span className={`${base} bg-green-50 text-green-700`}>Active</span>;
  if (status === "On Leave")
    return (
      <span className={`${base} bg-amber-50 text-amber-700`}>On Leave</span>
    );
  return (
    <span className={`${base} bg-slate-100 text-slate-500`}>Inactive</span>
  );
};

const LicenseBadge = ({ expiryDate }) => {
  const s = getLicenseStatus(expiryDate);
  if (!s) return null;
  if (s === "expired")
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-50 text-red-600">
        <AlertTriangle size={10} /> Expired
      </span>
    );
  if (s === "soon")
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-600">
        <AlertTriangle size={10} /> Expiring soon
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-50 text-green-600">
      <CheckCircle size={10} /> License valid
    </span>
  );
};

// ─── License image lightbox ──────────────────────────────────────────────────
const LicenseImageModal = ({ src, name, onClose }) => {
  if (!src) return null;
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl overflow-hidden max-w-lg w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-slate-100 flex justify-between items-center">
          <p className="font-semibold text-slate-800 text-sm">
            {name} — License Image
          </p>
          <button
            onClick={onClose}
            className="p-1 hover:bg-slate-100 rounded-full text-slate-400"
          >
            <XCircle size={20} />
          </button>
        </div>
        <div className="p-4 flex items-center justify-center bg-slate-50">
          <img
            src={src}
            alt="Driver License"
            className="max-w-full max-h-80 object-contain rounded-lg"
            onError={(e) => (e.target.src = licensePlaceholder)}
          />
        </div>
      </div>
    </div>
  );
};

// ==========================================
// HISTORY MODAL
// ==========================================
const DeletedHistoryModal = ({ isOpen, onClose, onRestore }) => {
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [auditItem, setAuditItem] = useState(null);

  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(
        `${domain}/api/LogisticsPersonnel/history?search=${search}`,
      );
      setHistoryData(data);
    } catch {
      toast.error("Failed to load history");
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    if (isOpen) {
      fetchHistory();
      setCurrentPage(1);
    }
  }, [isOpen, fetchHistory]);

  if (!isOpen) return null;

  const currentItems = historyData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-slate-100 flex justify-between items-center">
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <div className="p-2 bg-red-50 rounded-full text-red-500">
              <Trash2 size={18} />
            </div>
            Deleted History
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition"
          >
            <XCircle size={22} />
          </button>
        </div>
        {/* Search */}
        <div className="px-4 py-3 bg-slate-50/50">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search history..."
              className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-red-100 focus:border-red-400 text-sm outline-none"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 bg-slate-50/50">
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader />
            </div>
          ) : historyData.length === 0 ? (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-slate-100 mb-3">
                <History size={20} className="text-slate-400" />
              </div>
              <p className="text-slate-500 text-sm">
                No deleted records found.
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {/* Desktop table */}
              <div className="hidden sm:block bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <table className="w-full text-sm text-left text-slate-600">
                  <thead className="text-xs text-slate-500 uppercase bg-slate-50">
                    <tr>
                      <th className="px-4 py-3">EMP ID</th>
                      <th className="px-4 py-3">Name</th>
                      <th className="px-4 py-3">Role</th>
                      <th className="px-4 py-3">Deleted By</th>
                      <th className="px-4 py-3">Date</th>
                      <th className="px-4 py-3 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {currentItems.map((item) => (
                      <tr key={item.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <span className="font-mono text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-md">
                            {item.employeeId}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-medium text-slate-800">
                          <div className="flex items-center gap-1">
                            {getFullName(item)}
                            <button
                              onClick={() => setAuditItem(item)}
                              className="text-amber-500 hover:text-amber-700 ml-1"
                              title="Audit"
                            >
                              <Clock size={14} />
                            </button>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <RoleBadge role={item.role} />
                        </td>
                        <td className="px-4 py-3 text-xs font-medium text-red-500">
                          {item.deletedByFullName || "Unknown"}
                        </td>
                        <td className="px-4 py-3 text-red-500 text-xs">
                          {formatDate(item.deletedDate)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={async () => {
                              await onRestore(item.id);
                              fetchHistory();
                            }}
                            className="text-green-600 hover:text-green-700 font-semibold text-xs flex items-center justify-end gap-1 ml-auto"
                          >
                            <RotateCcw size={14} /> Restore
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile history cards */}
              <div className="sm:hidden flex flex-col gap-3">
                {currentItems.map((item) => (
                  <div
                    key={item.id}
                    className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <span className="font-mono text-[10px] bg-slate-100 text-slate-500 px-2 py-0.5 rounded">
                          {item.employeeId}
                        </span>
                        <h4 className="font-semibold text-slate-800 mt-1">
                          {getFullName(item)}
                        </h4>
                        <RoleBadge role={item.role} />
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setAuditItem(item)}
                          className="p-2 bg-amber-50 text-amber-500 rounded-lg hover:bg-amber-100"
                        >
                          <Clock size={16} />
                        </button>
                        <button
                          onClick={async () => {
                            await onRestore(item.id);
                            fetchHistory();
                          }}
                          className="p-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100"
                        >
                          <RotateCcw size={18} />
                        </button>
                      </div>
                    </div>
                    <div className="text-xs text-slate-500 mt-2">
                      <span className="flex items-center gap-1">
                        <Trash2 size={10} className="text-red-400" />
                        Deleted by{" "}
                        <span className="font-medium text-red-500 ml-1">
                          {item.deletedByFullName || "Unknown"}
                        </span>
                        <span className="ml-1">
                          · {formatDate(item.deletedDate)}
                        </span>
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-2">
                <Pagination
                  itemsPerPage={itemsPerPage}
                  totalItems={historyData.length}
                  currentPage={currentPage}
                  paginate={(p) => setCurrentPage(p)}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <AuditInfoModal
        isOpen={!!auditItem}
        onClose={() => setAuditItem(null)}
        title={getFullName(auditItem)}
        rows={buildAuditRows(auditItem)}
      />
    </div>
  );
};

// ==========================================
// MAIN COMPONENT
// ==========================================
const AllLogisticsPersonnel = () => {
  const currentUserID = useSelector(selectUserID);

  const [personnelData, setPersonnelData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [personnelToEdit, setPersonnelToEdit] = useState(null);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isHistoryModalVisible, setIsHistoryModalVisible] = useState(false);
  const [auditItem, setAuditItem] = useState(null);
  const [licenseViewer, setLicenseViewer] = useState(null); // { src, name }
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredData, setFilteredData] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${domain}/api/LogisticsPersonnel`);
      setPersonnelData(data);
    } catch {
      toast.error("Failed to fetch personnel.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const q = searchTerm.toLowerCase();
    setFilteredData(
      personnelData.filter(
        (item) =>
          (item.firstName || "").toLowerCase().includes(q) ||
          (item.lastName || "").toLowerCase().includes(q) ||
          (item.employeeId || "").toLowerCase().includes(q) ||
          (item.role || "").toLowerCase().includes(q),
      ),
    );
    setCurrentPage(1);
  }, [searchTerm, personnelData]);

  const handleSoftDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this record?")) return;
    try {
      await axios.put(
        `${domain}/api/LogisticsPersonnel/soft-delete/${id}`,
        { userID: currentUserID },
        { headers: { "Content-Type": "application/json" } },
      );
      toast.success("Record moved to History!");
      fetchData();
    } catch {
      toast.error("Failed to delete record.");
    }
  };

  const handleRestore = async (id) => {
    try {
      await axios.put(
        `${domain}/api/LogisticsPersonnel/restore/${id}`,
        { userID: currentUserID },
        { headers: { "Content-Type": "application/json" } },
      );
      toast.success("Record Restored Successfully!");
      fetchData();
    } catch {
      toast.error("Failed to restore record.");
    }
  };

  const currentItems = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  return (
    <div className="min-h-screen pb-20">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-slate-800 flex items-center gap-2">
                <Users className="text-indigo-600" /> Logistics Personnel
              </h1>
              <p className="text-xs md:text-sm text-slate-500 mt-1 hidden sm:block">
                Manage drivers and personnel records.
              </p>
            </div>
            <div className="flex gap-3 w-full md:w-auto">
              <button
                onClick={() => setIsHistoryModalVisible(true)}
                className="flex-1 md:flex-none flex justify-center items-center gap-2 bg-slate-50 border border-slate-200 text-slate-600 hover:bg-slate-100 text-sm font-medium px-4 py-2.5 rounded-xl transition-all"
              >
                <History size={18} /> History
              </button>
              <button
                onClick={() => {
                  setPersonnelToEdit(null);
                  setIsAddModalVisible(true);
                }}
                className="flex-1 md:flex-none flex justify-center items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-5 py-2.5 rounded-xl shadow-md shadow-indigo-200 transition-all"
              >
                <Plus size={18} /> Add New
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto py-2">
        {/* Stats + Search */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex items-center gap-4">
            <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl">
              <BarChart2 size={24} />
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                Active Personnel
              </p>
              <p className="text-2xl font-bold text-slate-800">
                {filteredData.length}
              </p>
            </div>
          </div>
          <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex items-center">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search by name, EMP ID, role..."
                className="w-full pl-10 pr-4 py-2 text-sm border-none ring-1 ring-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader />
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
              <table className="w-full text-sm text-left text-slate-600">
                <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-4 font-semibold">EMP ID</th>
                    <th className="px-6 py-4 font-semibold">Full Name</th>
                    <th className="px-6 py-4 font-semibold">Role</th>
                    <th className="px-6 py-4 font-semibold">Status</th>
                    <th className="px-6 py-4 font-semibold">License</th>
                    <th className="px-6 py-4 font-semibold">Contact</th>
                    <th className="px-6 py-4 font-semibold">Created Info</th>
                    <th className="px-6 py-4 font-semibold text-right">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {currentItems.map((item) => (
                    <tr key={item.id} className="hover:bg-slate-50 transition">
                      <td className="px-6 py-4">
                        <span className="font-mono text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-md whitespace-nowrap">
                          {item.employeeId}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2.5">
                          <div
                            className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs shrink-0 ${item.role === "Driver" ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-600"}`}
                          >
                            {getInitials(item)}
                          </div>
                          <span className="font-bold text-slate-800">
                            {getFullName(item)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <RoleBadge role={item.role} />
                      </td>
                      <td className="px-6 py-4">
                        <StatusBadge status={item.status} />
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col gap-1">
                          {item.licenseNumber && (
                            <span className="flex items-center gap-1 text-xs text-slate-500">
                              <CreditCard
                                size={11}
                                className="text-slate-400"
                              />
                              {item.licenseNumber}
                            </span>
                          )}
                          <LicenseBadge expiryDate={item.licenseExpiryDate} />
                          {item.licenseImage && (
                            <button
                              onClick={() =>
                                setLicenseViewer({
                                  src: `data:image/jpeg;base64,${item.licenseImage}`,
                                  name: getFullName(item),
                                })
                              }
                              className="text-[10px] text-indigo-500 hover:text-indigo-700 underline text-left"
                            >
                              View image
                            </button>
                          )}
                          {!item.licenseNumber && !item.licenseExpiryDate && (
                            <span className="text-slate-400 text-xs">—</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-xs text-slate-500">
                        <div className="flex flex-col gap-0.5">
                          {item.contactNumber && (
                            <span className="flex items-center gap-1">
                              <Phone size={10} className="text-slate-400" />
                              {item.contactNumber}
                            </span>
                          )}
                          {item.email && (
                            <span className="flex items-center gap-1">
                              <Mail size={10} className="text-slate-400" />
                              {item.email}
                            </span>
                          )}
                          {!item.contactNumber && !item.email && (
                            <span className="text-slate-400">—</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-xs">
                        <div className="flex flex-col">
                          <span className="font-semibold text-slate-700">
                            {item.createdByFullName || "System"}
                          </span>
                          <span className="text-slate-400">
                            {formatDate(item.createdDate)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => setAuditItem(item)}
                            className="p-2 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                            title="View Audit"
                          >
                            <Clock size={18} />
                          </button>
                          <button
                            onClick={() => {
                              setPersonnelToEdit(item);
                              setIsAddModalVisible(true);
                            }}
                            className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                          >
                            <Edit size={18} />
                          </button>
                          <button
                            onClick={() => handleSoftDelete(item.id)}
                            className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {currentItems.length === 0 && (
                    <tr>
                      <td
                        colSpan="8"
                        className="px-6 py-10 text-center text-slate-400"
                      >
                        No personnel found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="p-4 border-t border-slate-100 hidden md:block">
              <Pagination
                itemsPerPage={itemsPerPage}
                totalItems={filteredData.length}
                currentPage={currentPage}
                paginate={(p) => setCurrentPage(p)}
              />
            </div>

            {/* Mobile cards */}
            <div className="md:hidden flex flex-col gap-3">
              {currentItems.map((item) => (
                <div
                  key={item.id}
                  className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 flex flex-col gap-3"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-2.5">
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm shrink-0 ${item.role === "Driver" ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-600"}`}
                      >
                        {getInitials(item)}
                      </div>
                      <div>
                        <span className="font-mono text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">
                          {item.employeeId}
                        </span>
                        <h3 className="font-bold text-slate-800 text-base leading-tight mt-0.5">
                          {getFullName(item)}
                        </h3>
                        <div className="flex gap-1.5 mt-1 flex-wrap">
                          <RoleBadge role={item.role} />
                          <StatusBadge status={item.status} />
                          <LicenseBadge expiryDate={item.licenseExpiryDate} />
                        </div>
                      </div>
                    </div>
                    <span className="text-[10px] font-medium text-slate-400 bg-slate-50 px-2 py-1 rounded-full border border-slate-100 shrink-0">
                      {formatDate(item.createdDate)}
                    </span>
                  </div>

                  {(item.contactNumber || item.email || item.licenseNumber) && (
                    <div className="flex flex-col gap-1 text-xs text-slate-500 bg-slate-50 rounded-xl px-3 py-2">
                      {item.contactNumber && (
                        <span className="flex items-center gap-1.5">
                          <Phone size={11} className="text-slate-400" />{" "}
                          {item.contactNumber}
                        </span>
                      )}
                      {item.email && (
                        <span className="flex items-center gap-1.5">
                          <Mail size={11} className="text-slate-400" />{" "}
                          {item.email}
                        </span>
                      )}
                      {item.licenseNumber && (
                        <span className="flex items-center gap-1.5">
                          <CreditCard size={11} className="text-slate-400" />{" "}
                          {item.licenseNumber}
                          {item.licenseType && (
                            <span className="text-slate-400">
                              · {item.licenseType}
                            </span>
                          )}
                        </span>
                      )}
                      {item.licenseImage && (
                        <button
                          onClick={() =>
                            setLicenseViewer({
                              src: `data:image/jpeg;base64,${item.licenseImage}`,
                              name: getFullName(item),
                            })
                          }
                          className="text-[10px] text-indigo-500 hover:text-indigo-700 underline text-left mt-0.5"
                        >
                          View license image
                        </button>
                      )}
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-3 border-t border-slate-50">
                    <div className="flex items-center gap-1.5 text-xs text-slate-400 font-medium">
                      <User size={12} className="text-slate-300" />
                      {item.createdByFullName || "System"}
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setAuditItem(item)}
                        className="text-xs font-semibold text-amber-500 hover:text-amber-700 flex items-center gap-1"
                      >
                        <Clock size={14} /> Audit
                      </button>
                      <button
                        onClick={() => {
                          setPersonnelToEdit(item);
                          setIsAddModalVisible(true);
                        }}
                        className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                      >
                        <Edit size={14} /> Edit
                      </button>
                      <button
                        onClick={() => handleSoftDelete(item.id)}
                        className="text-xs font-semibold text-red-500 hover:text-red-700 flex items-center gap-1"
                      >
                        <Trash2 size={14} /> Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {currentItems.length === 0 && (
                <div className="text-center py-10 text-slate-400 bg-white rounded-2xl border border-slate-100">
                  No personnel found.
                </div>
              )}
              <div className="mt-2 pb-6">
                <Pagination
                  itemsPerPage={itemsPerPage}
                  totalItems={filteredData.length}
                  currentPage={currentPage}
                  paginate={(p) => setCurrentPage(p)}
                />
              </div>
            </div>
          </>
        )}
      </div>

      {/* Add/Edit Modal */}
      {isAddModalVisible && (
        <Addlogisticspersonnel
          onClose={() => {
            setIsAddModalVisible(false);
            fetchData();
          }}
          refreshData={fetchData}
          PersonnelToEdit={personnelToEdit}
        />
      )}

      {/* History Modal */}
      <DeletedHistoryModal
        isOpen={isHistoryModalVisible}
        onClose={() => {
          setIsHistoryModalVisible(false);
          fetchData();
        }}
        onRestore={handleRestore}
      />

      {/* Audit Modal */}
      <AuditInfoModal
        isOpen={!!auditItem}
        onClose={() => setAuditItem(null)}
        title={getFullName(auditItem)}
        rows={buildAuditRows(auditItem)}
      />

      {/* License Image Lightbox */}
      {licenseViewer && (
        <LicenseImageModal
          src={licenseViewer.src}
          name={licenseViewer.name}
          onClose={() => setLicenseViewer(null)}
        />
      )}
    </div>
  );
};

export default AllLogisticsPersonnel;
